import { Injectable } from '@angular/core';
import { Question, UserQuestionCategory } from '@common/dto/my';
import { createStore, setProp, withProps } from '@ngneat/elf';
import {
  addEntities,
  entitiesPropsFactory,
  getActiveId,
  getEntity,
  selectActiveEntity,
  selectActiveId,
  selectAllEntities,
  setActiveId,
  setEntities,
  upsertEntitiesById,
  withActiveId,
  withEntities,
} from '@ngneat/elf-entities';
import { Observable } from 'rxjs';

export const { categoriesEntitiesRef, withCategoriesEntities } =
  entitiesPropsFactory('categories');

interface QuestionnaireStateProps {
  error: any;
}

export type QuestionID = Question['questionId'];

@Injectable()
export class QuestionnaireRepository {
  activeQuestionId$: Observable<QuestionID>;
  activeQuestion$: Observable<Question | undefined>;
  userCategories$: Observable<UserQuestionCategory[]>;

  private store;

  constructor() {
    this.store = this.createStore();

    this.activeQuestionId$ = this.store.pipe(selectActiveId());
    this.activeQuestion$ = this.store.pipe(selectActiveEntity());
    this.userCategories$ = this.store.pipe(
      selectAllEntities({ ref: categoriesEntitiesRef })
    );
  }

  getActiveQuestion() {
    const activeId = this.store.query(getActiveId);
    return this.getQuestionById(activeId);
  }

  getActiveQuestionId() {
    return this.store.query(getActiveId);
  }

  getQuestionById(questionId: QuestionID) {
    return this.store.query(getEntity(questionId));
  }

  addQuestionAndSetActive(question: Question) {
    this.store.update(
      upsertEntitiesById(question.questionId, {
        creator: () => question,
        updater: () => question,
      }),
      setActiveId(question.questionId)
    );
  }

  addQuestion(question: Question) {
    this.store.update(addEntities(question));
  }

  setActiveId(questionId: QuestionID) {
    this.store.update(setActiveId(questionId));
  }

  setError(error: any) {
    this.store.update(setProp('error', error));
  }

  setCategories(categories: UserQuestionCategory[]) {
    this.store.update(setEntities(categories, { ref: categoriesEntitiesRef }));
  }

  private createStore(): typeof store {
    const store = createStore(
      { name: 'questionnaire' },
      withEntities<Question, 'questionId'>({
        idKey: 'questionId',
      }),
      withCategoriesEntities<UserQuestionCategory, 'tblQuestionCategoryId'>({
        idKey: 'tblQuestionCategoryId',
      }),
      withActiveId(),
      withProps<QuestionnaireStateProps>({ error: null })
    );

    return store;
  }
}
